// "How it works" intro modal — shown once after the wizard finishes,
// explains the Leitner box system + session controls. Dismissed by
// the close button or a backdrop click; persisted as "seen" in
// localStorage so repeat visits skip it.

import { DEFAULT_LIST_ID, getList } from "../config/lists";
import { STORAGE_KEYS } from "../config/storage-keys";
import { t } from "../i18n";
import { byId, hide, show } from "./dom";

/** Apply current locale to modal copy. Safe to call before open. */
export function localizeIntroModal(): void {
  const modal = byId("introModal");
  const titleEl = byId("introModalTitle");
  const closeBtn = byId("introModalCloseBtn");
  if (!modal || !titleEl || !closeBtn) return;
  titleEl.textContent = t("intro_title") as string;
  closeBtn.textContent = t("intro_close") as string;
  // List items are tagged with data-i18n keys. Titles are plain text;
  // .intro-sub entries contain <b>…</b> pastille tags so we set them
  // via innerHTML. Matches the legacy behavior exactly.
  modal.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
    const key = el.dataset.i18n;
    if (!key) return;
    const val = t(key) as string;
    if (el.classList.contains("intro-sub")) {
      el.innerHTML = val;
    } else {
      el.textContent = val;
    }
  });
  // The "Ton niveau CEFR s'adapte" bullet is language-specific. Hide
  // it on mono decks (capitales, future geography / history / …) where
  // CEFR doesn't apply.
  const activeList = getList(
    localStorage.getItem(STORAGE_KEYS.LIST_ID) || DEFAULT_LIST_ID,
  );
  const isMono = activeList.shape !== "bilingual";
  const cefrBullet = modal.querySelector<HTMLElement>(
    '[data-i18n="intro_b4_title"]',
  )?.closest("li") as HTMLElement | null;
  if (cefrBullet) {
    cefrBullet.classList.toggle("hidden", isMono);
  }
  // The "Écoute la prononciation" bullet is also audio-first: on mono
  // decks TTS defaults to OFF (both faces are in the user's own
  // language, hearing them spoken adds nothing), so promoting ♪ would
  // point at a feature they won't see. Hidden here too, matching b4.
  const ttsBullet = modal.querySelector<HTMLElement>(
    '[data-i18n="intro_b5_title"]',
  )?.closest("li") as HTMLElement | null;
  if (ttsBullet) {
    ttsBullet.classList.toggle("hidden", isMono);
  }
}

export function openIntroModal(): void {
  localizeIntroModal();
  show(byId("introModal"));
}

export function closeIntroModal(): void {
  hide(byId("introModal"));
  // Remember so we don't show this again on subsequent visits.
  localStorage.setItem(STORAGE_KEYS.INTRO, "1");
}

/** Wire close button + backdrop click. Call once on boot. */
export function installIntroModal(): void {
  const modal = byId("introModal");
  const closeBtn = byId("introModalCloseBtn");
  closeBtn?.addEventListener("click", closeIntroModal);
  modal?.addEventListener("click", (e) => {
    if (e.target === modal) closeIntroModal();
  });
}
